import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ParentTopic {
  parentTopicSlug: string;
  label: string;
  childTopicSlugs: string[];
  pageCount: number;
  effectivePageCountForCoverage?: number;
  totalWeight: number;
  averageWeight: number;
  coverageScore: number;
  breadthScore: number;
  depthScore: number;
  strengthScore: number;
  focusScore: number;
  authorityScore: number;
  // Enhanced coverage fields (optional)
  enhancedPageCountForCoverage?: number;
  relatedTopicsContribution?: number;
  relatedTopicsBreakdown?: Array<{
    topicSlug: string;
    similarity: number;
    pageCount: number;
    contribution: number;
  }>;
  // Link metrics (Phase 2 - optional, only if links enabled)
  contextualLinkCount?: number;
  structuralLinkCount?: number;
  linkScore?: number;
  // Explanation fields
  authorityExplanation?: {
    coverage?: string;
    strength?: string;
    focus?: string;
    links?: string;
  };
  // Topic classification
  topicTier?: 'owned' | 'aspirational' | 'ambient' | 'adjacent' | 'anti' | 'unclassified';
  topicTierReasoning?: string[];
}

export interface DomainTopicsResponse {
  success: boolean;
  data: {
    domain: string;
    parentTopics: ParentTopic[];
    message?: string;
  };
  timestamp: string;
}

export interface TopicLink {
  sourceUrl: string;
  sourcePageId: string;
  targetUrl: string;
  targetPageId: string;
  anchorText: string;
  linkType: string;
  confidence: number;
}

export interface TopicLinksResponse {
  success: boolean;
  data: {
    topic: string;
    domain: string;
    contextualLinks: TopicLink[];
    structuralLinks: TopicLink[];
    message?: string;
  };
  timestamp: string;
}

export interface TopicGraphNode {
  id: string;
  label: string;
  type: 'parentTopic' | 'grandparentTopic';
  grandparentTopic?: string;
  pathToGrandparent?: string[]; // Intermediate topics connecting this topic to its grandparent
  pageCount: number;
  authorityScore: number;
  exists: boolean;
}

export interface TopicGraphEdge {
  source: string;
  target: string;
  similarity: number;
  type: 'merged' | 'related' | 'somewhat-related';
  transitivePath?: string[]; // Intermediate topics that connect source and target
}

export interface TopicGraphResponse {
  success: boolean;
  data: {
    nodes: TopicGraphNode[];
    edges: TopicGraphEdge[];
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class DomainTopicsService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get all parentTopics for a domain
   */
  getDomainTopics(domain: string): Observable<DomainTopicsResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    return this.http.get<DomainTopicsResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics`);
  }

  /**
   * Get link details for a specific topic
   * Phase 2: Internal Links
   */
  getTopicLinks(domain: string, topicSlug: string): Observable<TopicLinksResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<TopicLinksResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/links`);
  }

  /**
   * Get topic graph showing relationships
   */
  getTopicGraph(domain: string, minSimilarity: number = 0.65): Observable<TopicGraphResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    return this.http.get<TopicGraphResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/graph`, {
      params: { minSimilarity: minSimilarity.toString() }
    });
  }

  /**
   * Get relationship tree for a specific topic
   */
  getTopicTree(domain: string, topicSlug: string, maxLevels: number = 3, maxPerLevel: number = 5): Observable<TopicTreeResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<TopicTreeResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/tree`, {
      params: { 
        maxLevels: maxLevels.toString(),
        maxPerLevel: maxPerLevel.toString()
      }
    });
  }

  /**
   * Get diagnostic data for a specific topic
   */
  getTopicDiagnostic(domain: string, topicSlug: string): Observable<DiagnosticResponse> {
    const normalizedDomain = domain.toLowerCase().trim();
    const normalizedTopicSlug = topicSlug.toLowerCase().trim();
    return this.http.get<DiagnosticResponse>(`${this.API_URL}/domains/${normalizedDomain}/topics/${normalizedTopicSlug}/diagnostic`);
  }
}

export interface TopicTreeNode {
  topicSlug: string;
  label: string;
  authorityScore: number;
  pageCount: number;
  relationshipType?: 'grandparent' | 'bridge' | 'merged' | 'related' | 'somewhat-related';
  similarity?: number;
  path?: string[];
  children?: TopicTreeNode[];
}

export interface TopicTreeResponse {
  success: boolean;
  data: {
    topic: {
      topicSlug: string;
      label: string;
      authorityScore: number;
      pageCount: number;
      grandparent?: string;
    };
    upLevels: TopicTreeNode[][];
    downLevels: TopicTreeNode[][];
    pathMetrics?: {
      pathLength: number;
      averageSimilarity?: number;
      weakestLink?: number;
      directSimilarityToGrandparent?: number;
    };
  };
  timestamp: string;
}

export interface DiagnosticResponse {
  success: boolean;
  data: {
    centerTopic: {
      parentTopicSlug: string;
      label: string;
      authorityScore: number;
      topicTier?: string;
      pageCount: number;
      coverageScore: number;
      breadthScore: number;
      depthScore: number;
      strengthScore: number;
      focusScore: number;
      linkScore?: number;
    };
    coverage: {
      directlyCoveredTopics: Array<{
        parentTopicSlug: string;
        label: string;
        pageCount: number;
        authorityScore: number;
        urlCount: number;
        topUrls: Array<{
          url: string;
          pageId: string;
          weight: number;
          salience: number;
        }>;
        hasMore: boolean;
      }>;
      strongestPages: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
        topicTags: Array<{
          topicSlug: string;
          label: string;
          weight: number;
        }>;
      }>;
      relatedTopics: Array<{
        parentTopicSlug: string;
        label: string;
        similarity: number;
        pageCount: number;
        contribution: number;
        urlCount: number;
        topUrls: Array<{
          url: string;
          pageId: string;
          weight: number;
          salience: number;
        }>;
        hasMore: boolean;
      }>;
    };
    strength: {
      totalWeight: number;
      averageWeight: number;
      strengthScore: number;
      topPagesByWeight: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
      }>;
    };
    focus: {
      averageWeight: number;
      focusScore: number;
      mostFocusedPages: Array<{
        url: string;
        pageId: string;
        weight: number;
        salience: number;
        relativeDensity: number;
      }>;
    };
    links: {
      contextualLinkCount?: number;
      structuralLinkCount?: number;
      linkScore?: number;
      topContextualLinks: Array<{
        sourceUrl: string;
        targetUrl: string;
        anchorText: string;
        linkType: string;
      }>;
    };
  };
  timestamp: string;
}

